import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { parseUtc } from '../lib/calendarDate';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { SkillPill } from '../components/SkillPill';
import { 
  Send, Loader2, Sparkles, Trash2, 
  Plus, Heart, MessageCircle, Calendar, ArrowRight,
  Lightbulb, Users, BookOpen, Coffee, Star, ExternalLink,
  PanelLeftClose, PanelLeftOpen
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Expert Card Component for AI recommendations
const ExpertCard = ({ userId, onNavigate }) => {
  const [expert, setExpert] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadExpert();
  }, [userId]);

  const loadExpert = async () => {
    try {
      const response = await axios.get(`${API}/ai/expert-card/${userId}`, { withCredentials: true });
      setExpert(response.data);
    } catch (error) {
      console.error('Error loading expert:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-gray-800 dark:to-gray-800 rounded-xl p-4 my-2 animate-pulse" data-testid={`expert-card-loading-${userId}`}>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-full" />
          <div className="flex-1">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-2" />
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-32" />
          </div>
        </div>
      </div>
    );
  }

  if (!expert) return null;

  // Brief 27 oct LOT F — tokens dark : avant, fond pastel clair + texte gris foncé
  // hérités du mode clair → carte illisible sur le fond sombre de MaIA.
  return (
    <div 
      className="bg-gradient-to-r from-purple-50 via-white to-pink-50 dark:from-gray-800 dark:via-gray-800 dark:to-gray-800 rounded-xl p-4 my-3 border border-purple-100 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-500 hover:shadow-lg dark:hover:shadow-purple-900/30 transition-all cursor-pointer group"
      onClick={() => onNavigate(`/app/profile/${userId}`)}
      data-testid={`expert-card-${userId}`}
    >
      <div className="flex items-center gap-4">
        <Avatar className="h-14 w-14 ring-2 ring-purple-200 dark:ring-purple-700 group-hover:ring-purple-400 transition-all">
          <AvatarImage src={expert.avatar} />
          <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white font-bold">
            {expert.display_name?.substring(0, 2).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="font-bold text-gray-900 dark:text-white group-hover:text-purple-700 dark:group-hover:text-purple-300 transition-colors">
              {expert.display_name}
            </h4>
            {expert.rating_avg > 0 && (
              <div className="flex items-center gap-1 text-amber-500 dark:text-amber-400">
                <Star className="h-3.5 w-3.5 fill-current" />
                <span className="text-xs font-medium">{expert.rating_avg.toFixed(1)}</span>
              </div>
            )}
          </div>
          
          {expert.bio && (
            <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-1 mt-0.5">{expert.bio}</p>
          )}
          
          {expert.skills?.length > 0 && (
            <div className="flex gap-1.5 mt-2 flex-wrap" data-testid="maia-expert-card-skills">
              {expert.skills.slice(0, 3).map((skill, i) => (
                <SkillPill key={i} size="xs" testId={`maia-expert-card-skill-${i}`}>
                  {skill}
                </SkillPill>
              ))}
            </div>
          )}
        </div>
        
        <div className="flex flex-col gap-2">
          <Button
            size="sm"
            className="bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-md hover:shadow-lg transition-all"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate(`/app/profile/${userId}?book=1`);
            }}
            data-testid={`expert-card-book-${userId}`}
          >
            <Calendar className="h-4 w-4 mr-1" />
            Réserver
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-purple-200 dark:border-purple-700 text-purple-600 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/30 dark:bg-transparent"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate(`/app/profile/${userId}`);
            }}
            data-testid={`expert-card-profile-${userId}`}
          >
            <ExternalLink className="h-4 w-4 mr-1" />
            Profil
          </Button>
        </div>
      </div>
      
      {expert.session_types?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-purple-100 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Sessions disponibles :</p>
          <div className="flex gap-2 flex-wrap">
            {expert.session_types.map((st, i) => (
              <span key={i} className="text-xs px-2 py-1 rounded-lg bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200">
                {st.title} • {st.is_free ? 'Gratuit' : `${st.price} CHF`}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const AIAssistant = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  // Brief Jun 15 — MaIA GaaS : context + suggestions injected at mount.
  const [maiaContext, setMaiaContext] = useState(null);
  const [dynamicSuggestions, setDynamicSuggestions] = useState([]);
  const messagesEndRef = useRef(null);

  const firstName = user?.display_name?.split(' ')[0] || 'toi';

  // Sprint 1.0c V1.a-bis (Bug 7) — collapsible sidebar, réduite par défaut.
  // Steve : "Toggle collapsible avec sidebar par défaut réduite (style ChatGPT)"
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Brief Jun 15 §2 — pick the right welcome template based on the live context.
  // Returns { greeting, bullets } where each bullet is a clickable chip.
  const buildWelcome = (ctx, fName) => {
    if (!ctx) {
      // Fallback static (context not yet loaded) — Jun 14 wording
      return {
        greeting: `Bonjour ${fName} ! 👋\n\nJe suis MaIA, ton guide OpenSapiens. Sur OpenSapiens, tu peux apprendre ET transmettre depuis le même compte — pas de mode à activer, les deux cohabitent.`,
        bullets: [
          { label: 'Trouver un expert', emoji: '🔎', prompt: 'Trouve-moi un expert qui peut m\'aider' },
          { label: 'Créer une session', emoji: '📅', prompt: 'Comment je crée une session ?' },
          { label: 'Compléter mon profil', emoji: '🛠️', prompt: 'Comment compléter mon profil rapidement ?' },
          { label: 'Rejoindre une communauté', emoji: '🤝', prompt: 'Comment rejoindre une communauté ?' },
        ],
      };
    }
    const skill = ctx.user.primarySkill || 'ton domaine';
    const completeness = ctx.user.profileCompleteness;
    const hasFuture = ctx.suggestions.hasFutureSession;
    const pendingInv = ctx.suggestions.pendingInvitations;
    const sessionsCompleted = ctx.activity.sessionsCompleted;
    const followers = ctx.activity.followers;

    // Cas 1 — Nouveau user (profil < 30%, 0 session)
    if (completeness < 30 && ctx.activity.sessionsCreated === 0) {
      return {
        greeting: `Bonjour ${fName} ! Je suis MaIA, ton guide OpenSapiens.\nTu viens d'arriver — commençons par te mettre en valeur.\n${skill !== 'ton domaine' ? `Avec ton expertise en ${skill}, tu peux apprendre ET transmettre depuis ce même compte.` : 'Sur OpenSapiens, tu peux apprendre ET transmettre depuis le même compte.'}`,
        bullets: [
          { label: 'Compléter mon profil', emoji: '→', prompt: 'Comment compléter mon profil rapidement ?' },
          { label: 'Créer ma première session', emoji: '→', prompt: 'Comment créer ma première session ?' },
          { label: 'Trouver un expert', emoji: '→', prompt: 'Trouve-moi un expert qui correspond à mes besoins' },
          { label: 'Découvrir les communautés', emoji: '→', prompt: 'Quelles sont les communautés actives à rejoindre ?' },
        ],
      };
    }
    // Cas 2 — Actif avec invitation ou session imminente
    if (pendingInv > 0 || hasFuture) {
      const nextTitle = ctx.activity.nextSession?.title;
      const nextDate = ctx.activity.nextSession?.date
        ? (parseUtc(ctx.activity.nextSession.date) || new Date(ctx.activity.nextSession.date)).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
        : null;
      let greeting = `Bonjour ${fName} !`;
      if (pendingInv > 0) greeting += ` Tu as ${pendingInv} invitation${pendingInv > 1 ? 's' : ''} en attente dans Mes sessions.`;
      if (hasFuture && nextTitle) greeting += ` Ta prochaine session « ${nextTitle} »${nextDate ? ` approche le ${nextDate}` : ''}.`;
      return {
        greeting,
        bullets: [
          ...(hasFuture ? [{ label: 'Préparer ma prochaine session', emoji: '→', prompt: 'Comment bien préparer ma session de demain ?' }] : []),
          ...(pendingInv > 0 ? [{ label: 'Gérer mes invitations', emoji: '→', prompt: `J'ai ${pendingInv} invitation${pendingInv > 1 ? 's' : ''} en attente, que faire ?` }] : []),
          { label: `Développer mon offre${skill !== 'ton domaine' ? ` en ${skill}` : ''}`, emoji: '→', prompt: `Comment développer mon offre${skill !== 'ton domaine' ? ` en ${skill}` : ''} ?` },
          { label: 'Trouver des apprenants', emoji: '→', prompt: 'Comment trouver des apprenants intéressés par mon expertise ?' },
        ].slice(0, 4),
      };
    }
    // Cas 3 — Profil complet, actif (default)
    return {
      greeting: `Bonjour ${fName} ! Tout roule — ${sessionsCompleted} session${sessionsCompleted > 1 ? 's' : ''} réalisée${sessionsCompleted > 1 ? 's' : ''} et ${followers} abonné${followers > 1 ? 's' : ''}.${skill !== 'ton domaine' ? `\nContinuons à faire grandir ton activité en ${skill}.` : ''}`,
      bullets: [
        { label: 'Créer un nouveau créneau', emoji: '→', prompt: 'Comment créer un nouveau créneau disponible ?' },
        { label: 'Partager sur LinkedIn', emoji: '→', prompt: 'Comment partager ma prochaine session sur LinkedIn ?' },
        { label: 'Ajouter du contenu', emoji: '→', prompt: 'Comment ajouter du contenu à ma boutique ?' },
        { label: 'Tendances cette semaine', emoji: '→', prompt: 'Quelles sont les tendances cette semaine ?' },
      ],
    };
  };

  useEffect(() => {
    loadSessions();
    // Brief Jun 15 — MaIA GaaS : fetch live context + dynamic suggestions in parallel.
    // The welcome message and quick-reply pills are personalized based on the
    // user's current activity (firstName, primarySkill, pendingInvitations, ...).
    // Bug fix Jun 15 pre-beta : retry context once if first call fails, log the
    // error to console so silent failures are diagnosable in prod.
    let alive = true;
    const fetchCtx = async () => {
      for (let attempt = 1; attempt <= 2; attempt++) {
        try {
          const r = await axios.get(`${API}/maia/context`, { withCredentials: true, timeout: 15000 });
          return r.data;
        } catch (e) {
          console.error(`[MaIA] context fetch attempt ${attempt}/2 failed:`, e?.response?.status, e?.message);
          if (attempt === 2) return null;
          await new Promise(r => setTimeout(r, 800));
        }
      }
      return null;
    };
    const fetchSugg = async () => {
      try {
        const r = await axios.get(`${API}/maia/suggestions`, { withCredentials: true, timeout: 15000 });
        return r.data?.suggestions || [];
      } catch (e) {
        console.error('[MaIA] suggestions fetch failed:', e?.response?.status, e?.message);
        return [];
      }
    };
    Promise.all([fetchCtx(), fetchSugg()]).then(([ctx, suggestions]) => {
      if (!alive) return;
      setMaiaContext(ctx);
      setDynamicSuggestions(suggestions);
      const welcome = buildWelcome(ctx, firstName);
      // §6 — Micro-celebration : prepend a congratulation message if the user
      // just hit a first-of-kind milestone. Consume the flag immediately so
      // it never fires twice.
      const ra = ctx?.recentActions || {};
      let celebrationContent = null;
      if (ra.firstBookingReceived) {
        celebrationContent = `🎉 Félicitations ${firstName} ! Tu viens de recevoir ta première réservation. C'est le début de quelque chose. Veux-tu qu'on prépare cette session ensemble ?`;
      } else if (ra.firstSessionCompleted) {
        celebrationContent = `🎉 Bravo ${firstName} ! Ta première session est dans la boîte. Comment veux-tu capitaliser sur cette dynamique ?`;
      } else if (ra.profileJustCompleted) {
        celebrationContent = `🎉 ${firstName}, ton profil est désormais complet à 80%+ ! Tu vas apparaître plus haut dans les recherches. Et maintenant ?`;
      }
      const msgs = [];
      msgs.push({
        role: 'assistant',
        content: welcome.greeting,
        bullets: welcome.bullets,
        timestamp: new Date().toISOString(),
      });
      if (celebrationContent) {
        msgs.push({
          role: 'assistant',
          content: celebrationContent,
          timestamp: new Date().toISOString(),
        });
      }
      setMessages(msgs);
      // Consume the one-shot flags so they don't replay on next visit
      if (celebrationContent) {
        axios.post(`${API}/maia/context/consume`, null, { withCredentials: true }).catch(() => {});
      }
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstName]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadSessions = async () => {
    try {
      const response = await axios.get(`${API}/ai/sessions`, { withCredentials: true });
      setSessions(response.data.sessions || []);
    } catch (error) {
      console.error('Error loading sessions:', error);
    }
  };

  const loadSession = async (sid) => {
    try {
      setLoading(true);
      // Fetch full session history from backend
      const response = await axios.get(`${API}/ai/sessions/${sid}`, { withCredentials: true });
      
      if (response.data.messages && response.data.messages.length > 0) {
        setSessionId(sid);
        // Load all messages from history
        const historyMessages = response.data.messages.map(msg => ({
          role: msg.role,
          content: msg.content,
          timestamp: msg.timestamp || new Date().toISOString()
        }));
        setMessages(historyMessages);
      } else {
        // Empty session, start fresh
        setSessionId(sid);
        setMessages([{
          role: 'assistant',
          content: `On reprend où on en était ! 💬\n\nComment puis-je t'aider ?`,
          timestamp: new Date().toISOString()
        }]);
      }
    } catch (error) {
      console.error('Error loading session:', error);
      // Fallback to basic message
      setSessionId(sid);
      setMessages([{
        role: 'assistant',
        content: `On reprend où on en était ! 💬\n\nComment puis-je t'aider ?`,
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setLoading(false);
    }
  };

  const startNewSession = () => {
    setSessionId(null);
    setMessages([{
      role: 'assistant',
      content: `Nouvelle conversation, nouvelles opportunités ! ✨\n\nDis-moi ce que tu recherches.`,
      timestamp: new Date().toISOString()
    }]);
  };

  const deleteSession = async (sid) => {
    try {
      await axios.delete(`${API}/ai/sessions/${sid}`, { withCredentials: true });
      setSessions(prev => prev.filter(s => s.session_id !== sid));
      if (sessionId === sid) {
        startNewSession();
      }
    } catch (error) {
      console.error('Error deleting session:', error);
    }
  };

  const sendMessage = async (text) => {
    const userMessage = (text || "").trim();
    if (!userMessage || loading) return;

    setInput('');
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    }]);

    setLoading(true);
    setIsTyping(true);

    try {
      const response = await axios.post(`${API}/ai/chat`, {
        message: userMessage,
        session_id: sessionId
      }, { withCredentials: true, timeout: 75000 });

      if (!sessionId && response.data.session_id) {
        setSessionId(response.data.session_id);
        loadSessions();
      }

      await new Promise(resolve => setTimeout(resolve, 500));

      // Brief Jun 15 §4 — Parse [ACTION:label:/route] markers and convert them
      // to clickable buttons rendered under the message text.
      // Tolerant regex (iter_53 finding) : the LLM sometimes drops the literal
      // "ACTION:" prefix and emits '[Label:/app/route]'. We accept both forms
      // as long as the route segment starts with a slash, which avoids matching
      // legitimate text like '[1: 2]'.
      const rawContent = response.data.message || response.data.response || '';
      const actions = [];
      const cleanedContent = rawContent.replace(
        /\[(?:ACTION:)?([^:\]\n]+):(\/[^\]\n]+)\]/g,
        (_, label, route) => {
          actions.push({ label: label.trim(), route: route.trim() });
          return '';
        }
      ).trim();

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: cleanedContent,
        timestamp: new Date().toISOString(),
        kb_cards: response.data.kb_cards || [],
        actions,
      }]);
    } catch (error) {
      // Bug fix Jun 15 pre-beta : log status + url for prod diagnosis ; sober
      // fallback message kept sober and actionable.
      console.error('[MaIA] /api/ai/chat failed:', error?.response?.status, error?.code, error?.message);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Je n'ai pas pu traiter ta demande cette fois. Réessaie dans quelques secondes — je suis là !`,
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setLoading(false);
      setIsTyping(false);
    }
  };

  const handleSend = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    await sendMessage(input);
  };

  // Enhanced content rendering with expert card detection
  const renderContent = (content) => {
    if (!content) return null;
    // First, split by expert card markers
    const expertCardRegex = /\[EXPERT_CARD:([^\]]+)\]/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = expertCardRegex.exec(content)) !== null) {
      // Add text before the expert card
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: content.substring(lastIndex, match.index)
        });
      }
      // Add expert card
      parts.push({
        type: 'expert_card',
        userId: match[1]
      });
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < content.length) {
      parts.push({
        type: 'text',
        content: content.substring(lastIndex)
      });
    }

    // Brief 27 oct LOT F — ponctuation orpheline après une carte (« [EXPERT_CARD:…]. »)
    parts.forEach((part, i) => {
      if (part.type === 'text' && i > 0 && parts[i - 1].type === 'expert_card') {
        part.content = part.content.replace(/^[\s.,;:!]+(?=\n|$)/, '').replace(/^\n+/, '');
      }
    });

    // If no expert cards found, just render text
    if (parts.length === 0) {
      parts.push({ type: 'text', content });
    }

    return parts.map((part, partIndex) => {
      if (part.type === 'expert_card') {
        return <ExpertCard key={`expert-${partIndex}`} userId={part.userId} onNavigate={navigate} />;
      }

      // Render text content
      const lines = part.content.split('\n');
      const elements = [];
      let listItems = [];
      
      const flushList = () => {
        if (listItems.length > 0) {
          elements.push(
            <ul key={`list-${elements.length}`} className="space-y-1 my-2">
              {listItems}
            </ul>
          );
          listItems = [];
        }
      };
      
      lines.forEach((line, i) => {
        let processedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong class="text-purple-700">$1</strong>');
        processedLine = processedLine.replace(/\*(.*?)\*/g, '<em>$1</em>');
        
        if (line.match(/^[-•✨📚🤝🎯💡]\s/) || line.startsWith('- ') || line.startsWith('• ')) {
          flushList();
          const content = line.replace(/^[-•✨📚🤝🎯💡]\s*/, '');
          listItems.push(
            <li key={`${partIndex}-${i}`} className="flex items-start gap-2">
              <span className="text-purple-500 mt-0.5">•</span>
              <span dangerouslySetInnerHTML={{ __html: content.replace(/\*\*(.*?)\*\*/g, '<strong class="text-purple-700">$1</strong>') }} />
            </li>
          );
          return;
        }
        
        flushList();
        
        if (line.trim() === '') {
          elements.push(<div key={`${partIndex}-${i}`} className="h-2" />);
          return;
        }
        
        elements.push(
          <p key={`${partIndex}-${i}`} className="leading-relaxed" dangerouslySetInnerHTML={{ __html: processedLine }} />
        );
      });
      
      flushList();
      return <React.Fragment key={`text-${partIndex}`}>{elements}</React.Fragment>;
    });
  };

  // Brief Jun 15 §3 — quickPrompts statiques supprimés ; les pills sont
  // désormais générées dynamiquement par GET /api/maia/suggestions et
  // stockées dans dynamicSuggestions (rendues plus bas).

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    // Brief Jul 27 2026 §4 — timestamps backend = UTC naïf : parseUtc.
    const date = typeof timestamp === 'string' ? (parseUtc(timestamp) || new Date(timestamp)) : new Date(timestamp);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="h-[calc(100vh-8rem)] flex bg-white dark:bg-gray-900 rounded-2xl shadow-lg overflow-hidden" data-testid="ai-assistant-page">
      {/* Collapsed rail (always visible on md+ when sidebar closed) — Sprint 1.0c V1.a-bis Bug 7 */}
      {!sidebarOpen && (
        <div className="hidden md:flex flex-col items-center w-14 border-r border-gray-200 dark:border-gray-700 bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 py-4 gap-3">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-xl hover:bg-purple-100 dark:hover:bg-purple-900/40 text-gray-600 dark:text-gray-300"
            title="Ouvrir l'historique"
            data-testid="maya-sidebar-open"
          >
            <PanelLeftOpen className="h-5 w-5" />
          </button>
          <button
            onClick={startNewSession}
            className="p-2 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-md"
            title="Nouvelle conversation"
            data-testid="maya-new-session-rail"
          >
            <Plus className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Expanded Sidebar — Sprint 1.0c V1.a-bis: dark-mode fix (Bug 3) + collapsible (Bug 7) */}
      <div className={`${sidebarOpen ? 'flex' : 'hidden'} w-72 border-r border-gray-200 dark:border-gray-700 flex-col bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 md:flex`} style={{ display: sidebarOpen ? 'flex' : 'none' }}>
        <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
          <Button
            onClick={startNewSession}
            className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg shadow-purple-200 hover:shadow-xl transition-all"
            data-testid="maya-new-session-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nouvelle
          </Button>
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 shrink-0"
            title="Réduire l'historique"
            data-testid="maya-sidebar-close"
          >
            <PanelLeftClose className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider px-2 mb-3">
            Conversations récentes
          </p>
          {sessions.length === 0 ? (
            <div className="text-center py-8">
              <MessageCircle className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
              <p className="text-sm text-gray-400 dark:text-gray-500">Aucune conversation</p>
            </div>
          ) : (
            sessions.map((session) => (
              <div
                key={session.session_id}
                className={`group flex items-center justify-between p-3 rounded-xl cursor-pointer mb-2 transition-all ${
                  sessionId === session.session_id 
                    ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-200 shadow-sm' 
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'
                }`}
                onClick={() => loadSession(session.session_id)}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{session.preview}</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{session.message_count} messages</p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteSession(session.session_id);
                  }}
                  className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-lg text-red-500 transition-opacity"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main Chat */}
      <div className="flex-1 flex flex-col bg-gradient-to-b from-purple-50/30 to-white dark:from-gray-800/50 dark:to-gray-900">
        {/* Header */}
        <div className="flex items-center gap-4 p-4 border-b border-gray-100 dark:border-gray-700 bg-white/80 dark:bg-gray-900/70 backdrop-blur-sm">
          <div className="relative">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 via-pink-500 to-rose-400 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-200">
              <Sparkles className="h-6 w-6 text-white" />
            </div>
            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white" />
          </div>
          <div>
            <h2 className="font-bold text-gray-900 dark:text-white text-lg">MaIA</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              En ligne • Matching & support OpenSapiens
            </p>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''} animate-fadeIn`}
            >
              {msg.role === 'assistant' ? (
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-md">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
              ) : (
                <Avatar className="h-10 w-10 flex-shrink-0 ring-2 ring-purple-100">
                  <AvatarImage src={user?.avatar} />
                  <AvatarFallback className="bg-gradient-to-br from-purple-100 to-pink-100 text-purple-600 font-medium">
                    {user?.display_name?.substring(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              )}
              <div className="flex flex-col max-w-[80%]">
                <div
                  className={`px-4 py-3 ${
                    msg.role === 'user'
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl rounded-tr-md shadow-lg shadow-purple-200'
                      : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-2xl rounded-tl-md shadow-md border border-gray-100 dark:border-gray-700'
                  }`}
                >
                  <div className="text-sm leading-relaxed">
                    {renderContent(msg.content)}
                  </div>
                </div>
                {/* Brief Jun 15 §2 — welcome bullets : clickable chips that
                    auto-send the linked prompt to MaIA. Only rendered on the
                    first (welcome) assistant message. */}
                {msg.role === 'assistant' && Array.isArray(msg.bullets) && msg.bullets.length > 0 && (
                  <div className="mt-3 space-y-1.5" data-testid={`maia-welcome-bullets-${idx}`}>
                    {msg.bullets.map((bullet, bIdx) => (
                      <button
                        key={bIdx}
                        type="button"
                        onClick={() => sendMessage(bullet.prompt)}
                        className="w-full text-left text-[13px] px-3 py-2 rounded-lg flex items-start gap-2 transition-colors"
                        style={{
                          background: 'var(--color-background-secondary, #f3f4f6)',
                          color: 'var(--color-text-primary, #111827)',
                          border: '0.5px solid var(--color-border-tertiary, #e5e7eb)',
                        }}
                        data-testid={`maia-welcome-bullet-${bIdx}`}
                      >
                        <span style={{ color: '#7F77DD' }} className="font-bold">{bullet.emoji}</span>
                        <span className="flex-1">{bullet.label}</span>
                      </button>
                    ))}
                  </div>
                )}
                {/* Brief Jun 15 §4 — [ACTION:label:/route] buttons parsed from
                    MaIA's reply. Outline violet, navigate on click. */}
                {msg.role === 'assistant' && Array.isArray(msg.actions) && msg.actions.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2" data-testid={`maia-actions-${idx}`}>
                    {msg.actions.map((act, aIdx) => (
                      <button
                        key={aIdx}
                        type="button"
                        onClick={() => navigate(act.route)}
                        className="text-[12px] font-semibold px-3 py-1.5 inline-flex items-center gap-1.5 hover:opacity-90 transition-opacity"
                        style={{
                          background: 'transparent',
                          color: '#7F77DD',
                          border: '1px solid #7F77DD',
                          borderRadius: 'var(--border-radius-md, 0.5rem)',
                        }}
                        data-testid={`maia-action-${idx}-${aIdx}`}
                      >
                        {act.label} <ArrowRight className="h-3 w-3" />
                      </button>
                    ))}
                  </div>
                )}
                {/* Sprint 1.0c V1.b — Item 14: KB how-to cards
                    V1.a-bis hotfix #4 (Bug 1) — light mode WCAG AA
                    V1.a-bis hotfix #5 (Bug A) — dark mode WCAG AA */}
                {msg.role === 'assistant' && msg.kb_cards && msg.kb_cards.length > 0 && (
                  <div className="mt-2 space-y-2" data-testid={`maya-kb-cards-${idx}`}>
                    {msg.kb_cards.map((card) => (
                      <button
                        key={card.id}
                        onClick={() => navigate(card.route || '/app/home')}
                        className="w-full text-left bg-amber-100 hover:bg-amber-200 border border-amber-300 rounded-xl px-3 py-2.5 transition-colors group dark:bg-amber-900/50 dark:hover:bg-amber-900/70 dark:border-amber-700"
                        data-testid={`maya-kb-card-${card.id}`}
                      >
                        <div className="flex items-start gap-2.5">
                          <BookOpen className="h-4 w-4 text-amber-900 mt-0.5 shrink-0 dark:text-amber-200" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-slate-900 dark:text-amber-50">{card.title}</p>
                            <p className="text-[11px] text-slate-700 line-clamp-2 mt-0.5 dark:text-amber-100/90">{String(card.preview || '').replace(/\*\*/g, '')}</p>
                            <p className="text-[10px] text-amber-900 mt-1 font-semibold group-hover:underline dark:text-amber-200">
                              {card.steps_count} étape{card.steps_count > 1 ? 's' : ''} → Y aller
                            </p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                <span className={`text-xs text-gray-400 mt-1 ${msg.role === 'user' ? 'text-right' : ''}`}>
                  {formatTime(msg.timestamp)}
                </span>
              </div>
            </div>
          ))}
          
          {isTyping && (
            <div className="flex gap-3 animate-fadeIn">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-md">
                <Sparkles className="h-5 w-5 text-white" />
              </div>
              <div className="bg-white dark:bg-gray-800 px-5 py-4 rounded-2xl rounded-tl-md shadow-md border border-gray-100 dark:border-gray-700">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Prompts — Brief Jun 15 §3 : suggestions personnalisées via
            /api/maia/suggestions. Au clic = envoi direct (auto-send). */}
        {messages.length <= 1 && dynamicSuggestions.length > 0 && (
          <div className="px-4 pb-3">
            <p className="text-xs font-medium text-gray-400 mb-3 flex items-center gap-2">
              <Coffee className="h-3.5 w-3.5" />
              Suggestions pour toi
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {dynamicSuggestions.map((sugg, idx) => (
                <button
                  key={sugg.intent || idx}
                  onClick={() => sendMessage(sugg.label)}
                  className="flex items-center gap-2 text-left text-sm px-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-purple-300 dark:hover:border-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all group"
                  data-testid={`maia-quick-prompt-${idx}`}
                  data-intent={sugg.intent}
                >
                  <Lightbulb className="h-4 w-4 text-purple-500 group-hover:scale-110 transition-transform shrink-0" />
                  <span className="text-gray-700 dark:text-gray-200 group-hover:text-purple-700 dark:group-hover:text-purple-300">{sugg.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <form onSubmit={handleSend} className="p-4 border-t border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900">
          <div className="flex items-center gap-3">
            <Input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Pose ta question à MaIA…"
              className="flex-1 bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 dark:text-white focus:bg-white dark:focus:bg-gray-800 focus:border-purple-300 rounded-xl pl-4 pr-4 py-3 transition-all"
              disabled={loading}
              data-testid="ai-input"
            />
            <Button
              type="submit"
              disabled={!input.trim() || loading}
              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl px-5 py-3 shadow-lg shadow-purple-200 hover:shadow-xl transition-all disabled:opacity-50"
              data-testid="ai-send-btn"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </Button>
          </div>
          <p className="text-xs text-center text-gray-400 mt-2">
            MaIA utilise l&apos;IA pour t&apos;aider
          </p>
        </form>
      </div>
    </div>
  );
};

export default AIAssistant;
